import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
// @mui
import { Container, Typography, Box, Stack, Alert } from '@mui/material';
// components
import { useNavigate, useSearchParams } from 'react-router-dom';
import { EventList } from '../sections/@dashboard/events';
import { useAppContext } from '../context';

// ----------------------------------------------------------------------

const API_URL = process.env.REACT_APP_API_URL || 'https://api.ginoboxingstudio.com';

// Normalise la forme Strapi v4 ({ id, attributes }) en objet plat pour EventCard
const normalizeEvent = (item) => {
  const attrs = item.attributes || {};
  const url = attrs.image?.data?.attributes?.url;
  return {
    id: item.id,
    title: attrs.title,
    description: attrs.description,
    price: attrs.price,
    totalSeats: attrs.totalSeats,
    bookedSeats: attrs.bookedSeats || 0,
    date: attrs.date,
    imageUrl: url ? (url.startsWith('http') ? url : `${API_URL}${url}`) : null,
  };
};

export default function EventsPage() {
  const { user } = useAppContext();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [bookedIds, setBookedIds] = useState([]);
  const [searchParams] = useSearchParams();
  const payment = searchParams.get('payment');

  useEffect(() => {
    if (!user) {
      navigate('/login', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user) {
      const myHeaders = new Headers();
      myHeaders.append('Authorization', `Bearer ${user.jwt}`);

      const requestOptions = {
        method: 'GET',
        headers: myHeaders,
        redirect: 'follow',
      };

      // Événements à venir uniquement (on masque les événements passés ; ceux sans
      // date restent visibles).
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);

      fetch(`${API_URL}/api/events?populate=image&sort[0]=date:asc`, requestOptions)
        .then((response) => response.json())
        .then((result) => {
          if (result.data) {
            const upcoming = result.data
              .map(normalizeEvent)
              .filter((e) => !e.date || new Date(e.date) >= startOfToday);
            setEvents(upcoming);
          }
        })
        .catch((error) => console.log('error', error));

      // Inscriptions de l'utilisateur courant (pour afficher « Déjà inscrit »)
      fetch(`${API_URL}/api/my-event-registrations`, requestOptions)
        .then((response) => response.json())
        .then((result) => {
          if (result?.eventIds) {
            setBookedIds(result.eventIds);
          }
        })
        .catch((error) => console.log('error', error));
    }
  }, [user]);

  return (
    <>
      <Helmet>
        <title>Événements : Gino Boxing</title>
      </Helmet>

      <Container>
        <Typography variant="h4" sx={{ mb: 3 }}>
          Événements
        </Typography>

        <Box sx={{ mb: 5 }}>
          <Box
            component="img"
            src="/assets/images/products/event-default.jpg"
            alt="Cours de boxe en plein air — Gino Boxing Studio"
            sx={{
              width: '100%',
              height: { xs: 200, sm: 300, md: 360 },
              objectFit: 'cover',
              borderRadius: 2,
              display: 'block',
            }}
          />
          <Stack spacing={2} sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.45 }}>
              Parce que la boxe ne s'arrête pas aux portes de la salle, nous vous proposons cet été des
              cours signés Gino Boxing Studio chez nos partenaires aixois.
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Retrouvez-nous tous les mardis matin au Domaine Gao, ainsi que chez Loucau pour quelques
              dates (voir planning).
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Un été sportif, en plein air, dans des lieux iconiques de la région, que nous avons hâte
              de partager à vos côtés ☀️🥊
            </Typography>
          </Stack>
        </Box>

        {payment === 'success' && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Paiement confirmé ! Votre inscription est enregistrée.
          </Alert>
        )}
        {payment === 'cancel' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Paiement annulé. Vous pouvez réessayer quand vous le souhaitez.
          </Alert>
        )}

        {events.length === 0 ? (
          <Box sx={{ py: 5 }}>
            <Typography variant="body1" color="text.secondary">
              Aucun événement disponible pour le moment.
            </Typography>
          </Box>
        ) : (
          <EventList events={events} bookedIds={bookedIds} />
        )}
      </Container>
    </>
  );
}
